import { AnalyticsProviderUser } from '@storefront-core/analytics-web';

import {
  recommendationConfig,
  RecommendationType,
} from '@/types/recommendation.types';

import { STORAGE_MESSAGE_TYPES } from './constants';
import { getRecommendationType } from './product.util';
import { postMessageToApp } from './window.util';

const SEARCH_ANALYTICS_HANDOFF_COOKIE = 'sf_search_analytics_handoff';
const WEB_COMMON_PROPS_COOKIE_NAME = 'makro_web_common_props';
export const USER_INFO_COOKIE = 'sf_user_info';
export enum SourceEvent {
  MANUAL_SEARCH = 'manualSearch',
  MULTI_LIST_SEARCH = 'multiListSearch',
  HISTORY_CLIKED = 'historyClick',
  PRODUCT_SUGGESTION = 'productSuggestion',
  QUERY_SUGGESTIONS = 'querySuggestion',
  TRENDING_WORDS = 'trendingWord',
  POPULAR_BRANDS = 'popularBrandsClick',
  FILTER_SELECTED = 'filterSelected',
  SORT_SELECTED = 'sortSelected',
  AUTO_CORRECTION = 'autoCorrection',
  AUTO_CATEGORISATION = 'autoCategorisation',
  ADD_TO_BASKET = 'addToBasket',
  ICON = 'icon',
  MENU = 'menu',
  SEARCH = 'search',
  RELATED_SEARCH_CLICKED = 'relatedSearchCick',
  INCREASE_ITEM = 'increaseItem',
  DECREASE_ITEM = 'decreaseItem',
  DELETE_ITEM = 'deleteItem',
}

export enum TriggerType {
  DIRECT_CLICK = 'directClick',
  ENTER = 'enter',
}

export enum SessionStorageAnalyticProps {
  SEARCH_PERFORM_ANALYTIC_PROPS = 'searchPerformAnalyticProps',
  PDP_CLICK_ANALYTIC_PROPS = 'pdpClickAnalyticProps',
  ADD_TO_BASKET_ANALYTIC_PROPS = 'addToBasketAnalyticProps',
  MULTI_SEARCH_ANALYTIC_PROPS = 'multiSearchAnalyticProps',
  COMMON_PROPS = 'commonProps',
  OTHER_BASKET_ANALYTIC_PROPS = 'otherBasketAnalyticProps',
  PRODUCT_SUGGESTION_PROPS = 'productSuggestionProps',
  FILTER_PERFORM_ANALYTIC_PROPS = 'filterPerformAnalyticProps',
}

type CookieOptions = {
  maxAgeSeconds?: number;
  path?: string;
  sameSite?: 'Lax' | 'Strict' | 'None';
  secure?: boolean;
};

const encode = (value: unknown): string => {
  return encodeURIComponent(JSON.stringify(value));
};

export const setSessionData = (key: string, data: unknown) => {
  if (data !== undefined) sessionStorage.setItem(key, JSON.stringify(data));
};

export const getSessionData = (key: string) => {
  return sessionStorage.getItem(key);
};

export const removeSessionData = (key: string) => {
  return sessionStorage.removeItem(key);
};

export const updateSessionData = <T extends Record<string, unknown>>(
  key: string,
  updates: Partial<T>
) => {
  const existingData = getSessionData(key);

  const existing: T = existingData ? JSON.parse(existingData) : ({} as T);

  setSessionData(key, {
    ...existing,
    ...updates,
  });
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(
      '(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'
    )
  );
  return match ? match[1] : null;
};

export const getSearchAnalyticsHandoff = (): unknown | null => {
  if (typeof document === 'undefined') return null;

  const raw = readCookie(SEARCH_ANALYTICS_HANDOFF_COOKIE);
  if (!raw) return null;

  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
};

export const getCurrentPageNameFromCookie = () =>{
  const cookie = getSearchAnalyticsHandoff() as {
    originPageName?: string;
  } | null;
  if (cookie?.originPageName) return cookie.originPageName;
  return undefined;
}
export const saveCheckoutItemAnalytics = (
  productId: string,
  query: string,
  pageName: string,
  isNative?: boolean
) => {
  try {
    let prevAnalyticsStr = sessionStorage.getItem('checkoutItemAnalytics');
    if (!prevAnalyticsStr) {
      const cookieStr = readCookie('checkoutItemAnalytics');
      if (cookieStr) prevAnalyticsStr = decodeURIComponent(cookieStr);
    }
    const prevAnalytics = prevAnalyticsStr ? JSON.parse(prevAnalyticsStr) : {};

    prevAnalytics[productId] = {
      query,
      pageName,
      sourceEvent: SourceEvent.SEARCH,
    };

    const updatedAnalyticsStr = JSON.stringify(prevAnalytics);
    sessionStorage.setItem('checkoutItemAnalytics', updatedAnalyticsStr);

    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const rootDomain =
      parts.length > 2 ? '.' + parts.slice(-3).join('.') : hostname;

    document.cookie = `checkoutItemAnalytics=${encodeURIComponent(updatedAnalyticsStr)}; Path=/; SameSite=Lax; Domain=${rootDomain}`;

    if (isNative) {
      postMessageToApp({
        type: STORAGE_MESSAGE_TYPES.SET_STORAGE,
        data: { key: 'checkoutItemAnalytics', value: updatedAnalyticsStr },
      });
    }
  } catch (e) {
    console.error('Failed to save checkout analytics', e);
  }
};

export const getCookieDomainForAnalytics = (): string => {
  // SSR-safe: client components are still server-rendered, where `window`
  // is undefined. Resolving the real domain only matters on the client where
  // the analytics cookie is actually written.
  if (typeof window === 'undefined') return 'localhost';

  const hostname = window.location.hostname;

  if (hostname === 'localhost') return hostname;
  return process.env.NEXT_PUBLIC_COOKIE_DOMAIN || '';
};

export const getCommonAnalyticsProperties = (): unknown | null => {
  if (typeof document === 'undefined') return null;

  const raw = readCookie(WEB_COMMON_PROPS_COOKIE_NAME);
  if (!raw) return null;

  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
};

export const getUserInfo = (): AnalyticsProviderUser | null => {
  if (typeof document === 'undefined') return null;

  const raw = readCookie(USER_INFO_COOKIE);
  if (!raw) return null;

  try {
    const userObject = JSON.parse(decodeURIComponent(raw));
    return {
      id: userObject.userId,
      email: userObject.email,
      name: userObject.name,
      phone: userObject.phone,
    };
  } catch {
    return null;
  }
};

export const toCamelCase = (str: string): string =>
  str
    ?.trim()
    .toLowerCase()
    .split(' ')
    .map((word, i) =>
      i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join('') ?? '';

export const setSearchAnalyticsHandoffCookie = (
  payload: unknown,
  options: CookieOptions = {}
) => {
  if (typeof document === 'undefined') return;
  const {
    // maxAgeSeconds = 30, ← remove this
    path = '/',
    sameSite = 'Lax',
    secure = typeof window !== 'undefined' &&
      window.location.protocol === 'https:',
  } = options;

  const rootDomain = getCookieDomainForAnalytics();
  const isLocalhost = rootDomain === 'localhost';

  const cookieParts = [
    `${SEARCH_ANALYTICS_HANDOFF_COOKIE}=${encode(payload)}`,
    `Path=${path}`,
    `SameSite=${sameSite}`,
    ...(!isLocalhost ? [`Domain=${rootDomain}`] : []),
  ];

  if (secure) cookieParts.push('Secure');
  document.cookie = cookieParts.join('; ');
};

export const getRecommendationAnalytics = (
  recommendation?: string | string[]
) => {
  const resolved = Array.isArray(recommendation)
    ? recommendation[0]
    : recommendation;
  const type =
    getRecommendationType(resolved) ?? RecommendationType.SEARCH_START_TOPPICKS;
  const config = recommendationConfig[type];
  return {
    pagename: config?.pagename ?? '',
    sourceEvent: config?.sourceEvent ?? '',
    type,
  };
};
